import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BadgeCheckIcon, Loader2Icon, MailIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SiteFooter } from '../components/SiteFooter';
import { ChoiceChips } from '../components/ChoiceChips';
import { ACTIVATION_FORM_ENDPOINT, activationPayload, ACTIVATION_SUCCESS_URL, ActivationAnswers, activationMailto, emptyAnswers, featureOptions, heardFromOptions, prescriptionOptions, recommendOptions } from '../data/activation';
import { trackCustom, identifyUser } from '../utils/tracking';
type Status = 'idle' | 'submitting' | 'success' | 'error' | 'not_connected';
const ownerActions = [{
  title: 'Need a backup X1?',
  body: 'Keep a spare charged in the truck.',
  to: '/#offer'
}, {
  title: 'Equip your crew',
  body: 'Business / crew pack, one order.',
  to: '/#company'
}, {
  title: 'Join GritVue Crew Rewards',
  body: 'Share GritVue. Get paid.',
  to: '/rewards'
}];
const inputClass = 'mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3 text-[16px] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none';
const labelClass = 'text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400';
export function Activate() {
  const [answers, setAnswers] = useState<ActivationAnswers>(emptyAnswers);
  const [status, setStatus] = useState<Status>('idle');
  const started = useRef(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    trackCustom('activation_page_visited');
  }, []);
  const update = (key: keyof ActivationAnswers, value: string) => {
    if (!started.current) {
      started.current = true;
      trackCustom('activation_started');
    }
    setAnswers(prev => ({
      ...prev,
      [key]: value
    }));
    if (status === 'error' || status === 'not_connected') setStatus('idle');
  };
  const canSubmit = answers.orderNumber.trim() !== '' && /\S+@\S+\.\S+/.test(answers.email);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || status === 'submitting') return;
    // No backend yet: say so. Nothing is stored and nothing is faked.
    if (!ACTIVATION_FORM_ENDPOINT) {
      setStatus('not_connected');
      trackCustom('activation_not_connected');
      return;
    }
    setStatus('submitting');
    try {
      const response = await fetch(ACTIVATION_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(activationPayload(answers))
      });
      if (!response.ok) throw new Error(String(response.status));
      const result = (await response.json().catch(() => ({}))) as {
        success?: string | boolean;
      };
      if (result.success === false || result.success === 'false') throw new Error('not_delivered');
      identifyUser({
        email: answers.email
      });
      trackCustom('activation_completed', {
        feature: answers.feature,
        heard_from: answers.heardFrom,
        recommend: answers.recommend,
        wears_prescription: answers.wearsPrescription
      });
      if (ACTIVATION_SUCCESS_URL) {
        window.location.href = ACTIVATION_SUCCESS_URL;
        return;
      }
      setStatus('success');
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch {
      setStatus('error');
    }
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1679" data-mp="j6zw7mye6gbe">
      <PageHeader data-id="element-1680" data-mp="e7mz89dt3sv3" />
      <main className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:grid lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:px-8 lg:py-20" data-id="element-1681" data-mp="e73obcmkq4qk">
        <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-1682" data-mp="99brq5azhug1">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1683" data-mp="49z2xf0jydat">Owner activation</p>
          <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.75rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1684" data-mp="23mtfntfawtbl">
            Activate your
            <br data-id="element-1685" data-mp="1yo9uvwcg1nkt" />
            <span className="text-grit-500" data-id="element-1686" data-mp="1ylnmxvln5622">GritVue X1™</span>
          </h1>
          <p className="mt-5 max-w-lg font-display text-xl font-bold uppercase leading-snug text-offwhite" data-id="element-1687" data-mp="26oxuxq9fadcp">
            Make it official. Register your X1. Unlock owner resources. Help us build better gear.
          </p>
          <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-steel-300" data-id="element-1688" data-mp="1wscod0hr8u6r">
            Takes about a minute. Your answers tell us who uses the X1 and what to build next.
          </p>
          <Link to="/verify" className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.18em] text-grit-500 underline-offset-4 hover:underline" data-id="element-1689" data-mp="1nvlpkgbtj18h">
            Verify your production batch
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1690" data-mp="1izn34ya1vtap" />
          </Link>
        </div>

        <div className="mt-10 lg:mt-0" data-id="element-1691" data-mp="14zvjagwurios">
          {status === 'success' ? <motion.div role="status" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-1692" data-mp="190xjazylfyrt">
              <div className="border-2 border-grit-500 bg-grit-500/10 p-6 sm:p-8" data-id="element-1693" data-mp="lec25ggddmzv">
                <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase text-grit-500" data-id="element-1694" data-mp="zduzde7n7dxe">
                  <BadgeCheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-1695" data-mp="uc5mdt0v0q6o" />
                  Your GritVue X1 is registered.
                </p>
                <p className="mt-2 text-[15px] text-steel-200" data-id="element-1696" data-mp="14bwghtlid368">Welcome to GritVue. Thanks for helping us build better gear.</p>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-id="element-1697" data-mp="1i1zyg8dcuapv">
                {ownerActions.map(item => <li key={item.title} data-id="element-1698" data-mp="1e27zmndrjixp">
                    <Link to={item.to} className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1699" data-mp="1s1h3cnks8kks">
                      <span data-id="element-1700" data-mp="kqt4suonikp9">
                        <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1701" data-mp="oqjq4ives8me">{item.title}</span>
                        <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1702" data-mp="tolkhzyabud6">{item.body}</span>
                      </span>
                      <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1703" data-mp="yhxylg5c8iro" />
                    </Link>
                  </li>)}
                <li data-id="element-1704" data-mp="18dre98ecssdo">
                  <a href="/#rx" className="flex h-full items-center justify-between gap-3 border border-white/12 bg-ink-900 p-4 transition-colors duration-150 ease-grit hover:border-grit-500" data-id="element-1705" data-mp="18czk820en2pi">
                    <span data-id="element-1706" data-mp="3e7r5jiu83szj">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1707" data-mp="3e8ke8pcqsskb">Add RX capability</span>
                      <span className="mt-0.5 block text-[13px] text-steel-400" data-id="element-1708" data-mp="3j8omjs3hygzg">Available now: RX Capability Insert, +$14.95</span>
                    </span>
                    <ArrowRightIcon className="h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1709" data-mp="36vbfk67m7mlz" />
                  </a>
                </li>
              </ul>
            </motion.div> : <form onSubmit={submit} noValidate className="grid gap-6 border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1710" data-mp="2wz8429qvibmd">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-1711" data-mp="3ulf6bczpfrvk">
                <div data-id="element-1712" data-mp="3pmccypt37a3f">
                  <label htmlFor="act-order" className={labelClass} data-id="element-1713" data-mp="3pnvvkn27tx8d">Order number</label>
                  <input id="act-order" required value={answers.orderNumber} onChange={e => update('orderNumber', e.target.value)} placeholder="#1001" autoComplete="off" className={inputClass} data-id="element-1714" data-mp="3kmzpc2ue5yzs" />
                </div>
                <div data-id="element-1715" data-mp="299dsdviait3p">
                  <label htmlFor="act-email" className={labelClass} data-id="element-1716" data-mp="299xbhfjofnjb">Email</label>
                  <input id="act-email" type="email" required value={answers.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com" autoComplete="email" className={inputClass} data-id="element-1717" data-mp="2567kkjfj4tv4" />
                </div>
              </div>

              <div className="grid gap-5 border-t border-white/10 pt-6" data-id="element-1718" data-mp="3vb2lwtms9bwb">
                <div data-id="element-1719" data-mp="3vd5k40y3abc4">
                  <label htmlFor="act-use" className={labelClass} data-id="element-1720" data-mp="44rkuxhrwb4z">What do you use your X1 for?</label>
                  <input id="act-use" value={answers.useCase} onChange={e => update('useCase', e.target.value)} placeholder="Engine bays, attic runs, night calls..." className={inputClass} data-id="element-1721" data-mp="rulz9jhqq7rc" />
                </div>
                <div data-id="element-1722" data-mp="i52gsexxwfx0">
                  <label htmlFor="act-trade" className={labelClass} data-id="element-1723" data-mp="i3rcme0l165d">What&apos;s your profession / trade?</label>
                  <input id="act-trade" value={answers.trade} onChange={e => update('trade', e.target.value)} placeholder="Electrician, mechanic, HVAC..." className={inputClass} data-id="element-1724" data-mp="n22lalf55am0" />
                </div>
                <div data-id="element-1725" data-mp="1flhm027cacrq">
                  <label htmlFor="act-problem" className={labelClass} data-id="element-1726" data-mp="1flrf3ynol01n">What problem did you most want X1 to solve?</label>
                  <input id="act-problem" value={answers.problem} onChange={e => update('problem', e.target.value)} placeholder="Holding a flashlight in my teeth..." className={inputClass} data-id="element-1727" data-mp="1kxenhweb4upo" />
                </div>
              </div>

              <ChoiceChips legend="Which feature mattered most?" options={featureOptions} value={answers.feature} onChange={v => update('feature', v)} data-id="element-1728" data-mp="1tqi1c5sv79ku" />
              <ChoiceChips legend="Where did you first hear about GritVue?" options={heardFromOptions} value={answers.heardFrom} onChange={v => update('heardFrom', v)} data-id="element-1729" data-mp="11yaj50xpnp4c" />
              <ChoiceChips legend="Would you recommend X1 to a coworker?" options={recommendOptions} value={answers.recommend} onChange={v => update('recommend', v)} data-id="element-1730" data-mp="1fzd7jkc6hbif" />
              <ChoiceChips legend="Do you wear prescription lenses?" options={prescriptionOptions} value={answers.wearsPrescription} onChange={v => update('wearsPrescription', v)} optional data-id="element-1731" data-mp="2rc5sab6jwzoz" />

              <div data-id="element-1732" data-mp="2bba2ioopux5s">
                <button type="submit" disabled={!canSubmit || status === 'submitting'} className="flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1733" data-mp="2bbsa2hgtwxt0">
                  {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1734" data-mp="1ino0vlz1u2lk" />}
                  {status === 'submitting' ? 'Activating...' : 'Activate my X1'}
                </button>
                {!canSubmit && <p className="mt-2 text-[12px] text-steel-500" data-id="element-1735" data-mp="3d2711hw4bu0f">Order number and a valid email are required.</p>}

                {status === 'error' && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.03] p-4 text-[14px] text-steel-200" data-id="element-1736" data-mp="3gjl2vow3oj39">
                    Something went wrong and your activation wasn&apos;t saved. Try again, or email support@gritvuegear.com.
                  </p>}
                {status === 'not_connected' && <div role="status" className="mt-4 border border-white/20 bg-white/[0.03] p-4" data-id="element-1737" data-mp="3u9y8vlpflnpy">
                    <p className="font-display text-lg font-bold uppercase text-offwhite" data-id="element-1738" data-mp="2xlsavhn0ltkn">Online activation is being connected</p>
                    <p className="mt-1 text-[14px] text-steel-300" data-id="element-1739" data-mp="31mlz6c4ddae2">
                      Your answers have not been saved yet. Send them by email and we&apos;ll register your X1 by hand.
                    </p>
                    <a href={activationMailto(answers)} className="mt-3 inline-flex items-center gap-2 border-2 border-grit-500 px-4 py-2.5 font-display text-base font-extrabold uppercase text-grit-500 transition-colors duration-150 ease-grit hover:bg-grit-500 hover:text-ink-950" data-id="element-1740" data-mp="3bif8lmmsknrm">
                      <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-1741" data-mp="3sd7yspud94rn" />
                      Email my activation
                    </a>
                  </div>}
                <p className="mt-4 text-[12px] leading-relaxed text-steel-500" data-id="element-1742" data-mp="65hv8ld849l2">
                  We use your answers to support your X1 and improve our products and advertising. We never sell your data.{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2 hover:text-steel-300" data-id="element-1743" data-mp="39mgtq0hew0j2">Privacy policy</Link>
                </p>
              </div>
            </form>}
        </div>
      </main>
      <SiteFooter data-id="element-1744" data-mp="3jj3dely04lji" />
    </div>;
}